/**
 * <s-install-prompt> — "Install our app" card for repeat mobile visitors.
 *
 * Shows on phones unless the store is already open as an installed app
 * or the visitor dismissed it in the last 14 days.
 * - Browsers that support install prompts (Android Chrome and similar): the Install button opens the native prompt.
 * - iPhone/iPad Safari: shows Share → Add to Home Screen instructions (no programmatic install there).
 * - Other browsers with no native prompt after 3.5s: shows browser-menu → Install app instructions.
 *
 * Commands (button command attribute, commandfor this element's id):
 *   --install  open the native install prompt
 *   --dismiss  hide for 14 days
 */
const VISITS_KEY = 'vv-app-visits';
const SESSION_KEY = 'vv-app-session';
const DISMISSED_KEY = 'vv-app-dismissed';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

class SInstallPrompt extends HTMLElement {
  #ac;
  #deferred = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    if (this.#isInstalled() || this.#isSnoozed()) return;
    this.#countVisit();

    this.addEventListener('command', (event) => {
      if (event.command === '--install') this.#install();
      if (event.command === '--dismiss') this.#dismiss();
    }, opts);

    if (this.#isIos()) {
      this.dataset.mode = 'ios';
      this.hidden = false;
      return;
    }

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.#deferred = event;
      this.dataset.mode = 'native';
      this.hidden = false;
    }, opts);

    // No native prompt offered yet: show browser-menu instructions instead, so there is always a way to install.
    const fallback = setTimeout(() => {
      if (this.#deferred) return;
      this.dataset.mode = 'manual';
      this.hidden = false;
    }, 3500);
    this.#ac.signal.addEventListener('abort', () => clearTimeout(fallback));

    window.addEventListener('appinstalled', () => {
      this.hidden = true;
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #install() {
    if (!this.#deferred) return;
    this.#deferred.prompt();
    try {
      await this.#deferred.userChoice;
    } catch {
      // Prompt closed without a choice.
    }
    this.#deferred = null;
    this.hidden = true;
  }

  #dismiss() {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private mode); hide for this page only.
    }
    this.hidden = true;
  }

  #isInstalled() {
    return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
  }

  #isIos() {
    const ua = navigator.userAgent;
    const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return /iphone|ipad|ipod/i.test(ua) || iPadOS;
  }

  #isSnoozed() {
    try {
      const at = Number(localStorage.getItem(DISMISSED_KEY));
      return Boolean(at) && Date.now() - at < SNOOZE_MS;
    } catch {
      return true;
    }
  }

  #countVisit() {
    try {
      let visits = Number(localStorage.getItem(VISITS_KEY)) || 0;
      if (!sessionStorage.getItem(SESSION_KEY)) {
        visits += 1;
        localStorage.setItem(VISITS_KEY, String(visits));
        sessionStorage.setItem(SESSION_KEY, '1');
      }
      return visits;
    } catch {
      return 0;
    }
  }
}

if (!customElements.get('s-install-prompt')) customElements.define('s-install-prompt', SInstallPrompt);
